/**
 * Cascaded shadow maps for the walkaround: the three DirectionalLights whose
 * depth maps `PLOT_CSM_GLSL` samples, armed ahead of the mount so the plot's
 * program links against the shadow census the descent later draws with, and
 * repositioned every SURFACE tick around the orbit's own ground point.
 *
 * Reads the closure it was cut out of through `CascadesDeps`. The
 * `atlasCamera` probe's `shadowDebug` getter mixes this seam's state with
 * bake-pass state another seam owns, so it stays in atlas.ts and reads the
 * cascade half through the handle rather than folding here.
 */

import * as THREE from 'three';

import type { PlotShadowRef } from './dtmPlot';
import type { AtlasLive } from '../atlas';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { FarFieldHandle } from './farField.ts';
import type { PlotSunHandle } from './plotSun.ts';

export interface CascadesDeps {
  readonly atlasRoot: THREE.Object3D;
  readonly renderer: THREE.WebGLRenderer;
  readonly plotSun: PlotSunHandle;
  own(fn: () => void): void;
  readonly live: AtlasLive;
  readonly cameraRig: CameraRigHandle;
  /** The plot's frame carried out to the drawn one: the boxes and the anchor
   *  are compared against `vWorldPos`, which is post-`modelMatrix`. */
  readonly farField: FarFieldHandle;
  readonly dojoPaints: DojoPaintsHandle;
}

export interface CascadesHandle {
  plotShadowRef(): PlotShadowRef;
  armCascades(): void;
  updateShadowCascades(): void;
  /** Read by the `atlasCamera` probe's own `shadowDebug` getter, which stays
   *  in atlas.ts (it also reads bake-pass state this seam does not own). */
  readonly csm0: THREE.DirectionalLight | null;
  readonly csmPasses: number;
  readonly CSM_SPLIT_M: readonly [number, number];
  readonly CSM_HALF_SIZES_M: readonly [number, number, number];
  readonly _csmProbe: THREE.Vector3;
  readonly _csmCoord: THREE.Vector3;
}

export function createCascades(deps: CascadesDeps): CascadesHandle {
  // `cameraRig`, `farField` and `dojoPaints` are not destructured here: all
  // three are declared after this call site in atlas.ts's own closure, so a
  // getter that reads them only defers a TDZ crash to the first tick unless
  // the read stays behind `deps.` at its actual use site instead of an eager
  // destructure.
  const { atlasRoot, renderer, own } = deps;
  const { plotSunDirLocal } = deps.plotSun;

  /** Depth map size per cascade. `PLOT_CSM_GLSL`'s 3x3 PCF spacing is one
   *  texel of it, which is why it reaches the shader as a uniform rather than
   *  being written twice. */
  const CSM_MAP_PX = 1024;

  /**
   * The cascaded shadow chunk's own uniform bag (docs/roadmap/05-atmosphere-
   * and-light.md, "Shadows"): three depth maps and their light-space matrices,
   * the view-distance splits that pick between them, and the on/off switch
   * `PLOT_CSM_GLSL` reads before touching a texture at all. Built once, like
   * `atmoTerrainUniforms`, because `DtmPlot`'s own uniform bag is assembled
   * once at construction and has to find these objects already here; the
   * lights and render targets whose state flows into it come and go with the
   * SURFACE regime (`updateShadowCascades`), but the bag itself does not.
   */
  const csmUniforms: Record<string, THREE.IUniform> = {
    uCsmMap0: { value: null }, uCsmMap1: { value: null }, uCsmMap2: { value: null },
    uCsmMatrix0: { value: new THREE.Matrix4() },
    uCsmMatrix1: { value: new THREE.Matrix4() },
    uCsmMatrix2: { value: new THREE.Matrix4() },
    uCsmSplits: { value: new THREE.Vector2(0, 0) },
    // The point the boxes are centred on - `orbit.lookAt`, not the eye: a
    // walkaround orbits a ground point from several body-lengths away, and a
    // box centred on the eye instead put the contact patch under a vehicle in
    // the coarsest cascade while the eye's own empty air sat in the finest one.
    uCsmAnchor: { value: new THREE.Vector3() },
    uCsmOn: { value: 0 },
    uCsmTexel: { value: 1 / CSM_MAP_PX },
  };

  /** Always given to a new DtmPlot: the bag starts inert (`uCsmOn` 0) and
   *  stays correct whether or not a cascade has ever existed. */
  function plotShadowRef(): PlotShadowRef {
    return { uniforms: csmUniforms };
  }

  // ── cascaded shadow maps (docs/roadmap/05-atmosphere-and-light.md,
  // "Shadows") ────────────────────────────────────────────────────────────
  //
  // Three DirectionalLights, one per cascade, none of them lighting anything
  // through three's own PBR path (intensity 0 - the aerial/sky chunks own
  // every body's actual shading): each exists only for its shadow camera and
  // the depth map that camera bakes, which `PLOT_CSM_GLSL` samples directly by
  // reference. Built lazily on the first SURFACE tick rather than at mount, so
  // a session that never walks around never allocates three 1024^2 render
  // targets, and freed the moment SURFACE is left (`updateShadowCascades`)
  // rather than held for the rest of the session.
  // "Roughly 5m, 40m, 400m" read as each box's own reach from its anchor, not
  // its full width: the rover's own hull already spans a metre and a half
  // from its centre, and the --shadow arm's contact annulus reaches to
  // 2.6m of that - inside a 2.5m half-size the split sat astride the very
  // ring being read, and the two cascades' independent depth maps disagreed
  // by enough to rotate the darkest sector a bin off the sun's own azimuth.
  const CSM_HALF_SIZES_M = [5, 40, 400] as const;
  /** Where csmVisibility() switches maps, in metres - inside each box's own
   *  half-size with margin, so nothing the near arm reads straddles a seam
   *  between two independently-rendered depth maps. */
  const CSM_SPLIT_M = [4.0, 30.0] as const;

  let csm0: THREE.DirectionalLight | null = null;
  let csm1: THREE.DirectionalLight | null = null;
  let csm2: THREE.DirectionalLight | null = null;
  let csmWasInSurface = false;
  const _csmPrevAnchor = new THREE.Vector3(NaN, NaN, NaN);
  const _csmPrevSun = new THREE.Vector3(NaN, NaN, NaN);
  const _csmProbe = new THREE.Vector3();
  const _csmCoord = new THREE.Vector3();
  const _csmAnchorW = new THREE.Vector3();
  const _csmSunW = new THREE.Vector3();
  /** Shadow passes asked for since mount, for `shadowDebug` - the number that
   *  says whether anything is being drawn into the three maps at all. */
  let csmPasses = 0;

  function makeCascadeLight(): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight(0xffffff, 0);
    light.castShadow = true;
    light.visible = false;
    light.shadow.mapSize.set(CSM_MAP_PX, CSM_MAP_PX);
    // Pre-created rather than left to WebGLShadowMap's own lazy allocation, so
    // the uniform below is never bound to "no texture" and dispose() below is
    // the one place that frees it.
    light.shadow.map = new THREE.WebGLRenderTarget(CSM_MAP_PX, CSM_MAP_PX, {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat,
    });
    light.shadow.map.texture.name = 'csm-cascade';
    atlasRoot.add(light);
    atlasRoot.add(light.target);
    return light;
  }

  function ensureCascades(): void {
    if (csm0) return;
    csm0 = makeCascadeLight();
    csm1 = makeCascadeLight();
    csm2 = makeCascadeLight();
    csmUniforms['uCsmMap0']!.value = csm0.shadow.map!.texture;
    csmUniforms['uCsmMap1']!.value = csm1.shadow.map!.texture;
    csmUniforms['uCsmMap2']!.value = csm2.shadow.map!.texture;
    csmUniforms['uCsmMatrix0']!.value = csm0.shadow.matrix;
    csmUniforms['uCsmMatrix1']!.value = csm1.shadow.matrix;
    csmUniforms['uCsmMatrix2']!.value = csm2.shadow.matrix;
  }

  /**
   * The cascade lights on before the mesh they light exists.
   *
   * three counts shadow-casting lights into every material's program cache key,
   * and `updateShadowCascades` only turns these on once `plot` is set - so a
   * program linked before the mount was keyed on 0 cascades and the mount's
   * first draw linked a second one, keyed on 3, at 100 ms. Arming them first is
   * what makes the warmed program the program the mount uses. They are
   * intensity 0 and `shadowMap.autoUpdate` is off, so nothing is drawn into
   * their maps until `updateShadowCascades` asks.
   */
  function armCascades(): void {
    ensureCascades();
    csm0!.visible = true;
    csm1!.visible = true;
    csm2!.visible = true;
  }

  function disposeCascades(): void {
    for (const l of [csm0, csm1, csm2]) {
      if (!l) continue;
      l.shadow.dispose();
      atlasRoot.remove(l.target);
      atlasRoot.remove(l);
    }
    csm0 = csm1 = csm2 = null;
    csmUniforms['uCsmMap0']!.value = null;
    csmUniforms['uCsmMap1']!.value = null;
    csmUniforms['uCsmMap2']!.value = null;
    csmUniforms['uCsmOn']!.value = 0;
    // Forces the next SURFACE entry to reposition the (freshly recreated)
    // lights even if the camera and the sun have not moved since the exit.
    _csmPrevAnchor.set(NaN, NaN, NaN);
    _csmPrevSun.set(NaN, NaN, NaN);
  }
  own(() => disposeCascades());

  /** Aim one cascade's box at the walkaround camera, sized in real metres. */
  function positionCascade(
    light: THREE.DirectionalLight, halfSizeM: number, anchor: THREE.Vector3, sun: THREE.Vector3,
  ): void {
    const halfSizeW = halfSizeM;
    const dist = halfSizeW * 4;
    light.position.copy(anchor).addScaledVector(sun, dist);
    light.target.position.copy(anchor);
    light.target.updateMatrixWorld();
    const cam = light.shadow.camera;
    cam.left = -halfSizeW;
    cam.right = halfSizeW;
    cam.top = halfSizeW;
    cam.bottom = -halfSizeW;
    cam.near = Math.max(0.0005, dist - halfSizeW * 3);
    cam.far = dist + halfSizeW * 3;
    cam.updateProjectionMatrix();
  }

  /**
   * SURFACE-only, every tick: creates/frees the cascades at the regime edge,
   * flips the casters' `castShadow` with it, and repositions the three boxes
   * - which also raises `shadowMap.needsUpdate`, the one signal that actually
   * runs a shadow pass - only when the camera or the sun actually moved.
   * `setShadowDebug({ csm: false })` is the negative control: it leaves the
   * cascades running (so toggling it back costs nothing) and only zeroes
   * `uCsmOn`, the one switch `PLOT_CSM_GLSL` checks before it samples anything.
   */
  function updateShadowCascades(): void {
    const inSurface = deps.cameraRig.surfaceRegime() && deps.live.plot !== null;
    deps.live.plot?.setShadowCasting(inSurface);
    if (inSurface !== csmWasInSurface) {
      csmWasInSurface = inSurface;
      // NOT freed on the way out. Three shadow-casting lights are an input to
      // every non-raw material's program cache key, so retiring them relinked
      // the globe, the rings and every tile variant on the ascent - the same
      // six programs `warmDescentPrograms` exists to link once. The maps cost
      // nothing while `shadowMap.needsUpdate` stays false, and they go back
      // with the Atlas. The anchor still resets, so the next SURFACE entry
      // repositions the boxes rather than trusting a stale match.
      if (!inSurface) { _csmPrevAnchor.set(NaN, NaN, NaN); _csmPrevSun.set(NaN, NaN, NaN); }
    }
    const on = inSurface && deps.dojoPaints.shadowDebug.csmOverride !== false;
    csmUniforms['uCsmOn']!.value = on ? 1 : 0;
    if (!inSurface) return;
    ensureCascades();
    csm0!.visible = true;
    csm1!.visible = true;
    csm2!.visible = true;
    // The ground point the walkaround orbits, not the eye: the eye can sit a
    // dozen metres off at a working pitch and altitude, which put the contact
    // patch under a vehicle in the coarsest cascade while the empty air past
    // the eye sat in the finest one.
    //
    // In the DRAWN frame, because the three lights are scene children and the
    // shader compares the anchor against `vWorldPos`. That frame moves with the
    // eye now that the plot is registered, so the boxes are re-aimed and the
    // maps rebaked whenever the view moves - which is what the plot's own
    // anchor alone used to say, and no longer does on a pure turn in place.
    const anchor = deps.farField.plotFrameToWorld(_csmAnchorW.copy(deps.cameraRig.orbit.lookAt));
    const sun = deps.farField.plotDirToWorld(_csmSunW.copy(plotSunDirLocal));
    const moved = !anchor.equals(_csmPrevAnchor) || !sun.equals(_csmPrevSun);
    if (!moved) return;
    _csmPrevAnchor.copy(anchor);
    _csmPrevSun.copy(sun);
    positionCascade(csm0!, CSM_HALF_SIZES_M[0], anchor, sun);
    positionCascade(csm1!, CSM_HALF_SIZES_M[1], anchor, sun);
    positionCascade(csm2!, CSM_HALF_SIZES_M[2], anchor, sun);
    (csmUniforms['uCsmSplits']!.value as THREE.Vector2).set(
      CSM_SPLIT_M[0], CSM_SPLIT_M[1]);
    (csmUniforms['uCsmAnchor']!.value as THREE.Vector3).copy(anchor);
    renderer.shadowMap.needsUpdate = true;
    csmPasses++;
    deps.live.needsRender = true;
  }

  return {
    plotShadowRef,
    armCascades,
    updateShadowCascades,
    get csm0() { return csm0; },
    get csmPasses() { return csmPasses; },
    CSM_SPLIT_M,
    CSM_HALF_SIZES_M,
    _csmProbe,
    _csmCoord,
  };
}
